import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a Dutch invoice (factuur) include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Dutch invoice (factuur) must include: your business name and address; your BTW-nummer (VAT number) if registered; the client name and address; a unique invoice number; the invoice date; an itemized description of goods or services; the net amount, VAT rate (21% or 9%), VAT amount, and total including VAT. For B2B supplies within the EU, the VAT number of the buyer must also be shown.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the BTW (VAT) rate in the Netherlands?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Netherlands has two BTW (Belasting over de Toegevoegde Waarde) rates. The standard rate is 21%, which applies to most goods and services. The reduced rate of 9% applies to food, books, medicines, and some labor-intensive services. Exports outside the EU are zero-rated at 0% BTW. BTW registration is required for most businesses operating in the Netherlands.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the KOR (kleineondernemersregeling) in the Netherlands?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The KOR (kleineondernemersregeling) is the Dutch small business exemption allowing businesses with annual Dutch turnover below EUR 20,000 to opt out of BTW (VAT) registration. If you apply for KOR, you do not charge BTW on your invoices and cannot reclaim input BTW. KOR must be applied for at the Belastingdienst (Dutch Tax Authority).',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Dutch freelancers (ZZP-ers) invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Dutch ZZP-ers (zelfstandigen zonder personeel) billing EU business clients apply the reverse charge mechanism — the client self-accounts for BTW. For non-EU clients, services are zero-rated. Include your BTW-nummer and note "BTW verlegd" (reverse charge) on EU B2B invoices. Use the international invoice type to bill in EUR, USD, or GBP with your IBAN.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Dutch users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Dutch businesses, freelancers, and ZZP-ers. Create facturen (invoices) with 21% BTW auto-calculated, or standard invoices under KOR. Download a professional PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'BTW Invoice (Factuur)',
    subtitle: 'For BTW-registered Dutch businesses',
    features: ['21% BTW auto-calculated', 'BTW-nummer field', 'Net + BTW + total', 'Belastingdienst-compliant', 'EUR currency'],
    to: '/create/vat',
    color: 'orange',
    btnClass: 'bg-orange-600 hover:bg-orange-700',
  },
  {
    icon: FileText,
    title: 'KOR / Standard Invoice',
    subtitle: 'For ZZP-ers below EUR 20,000',
    features: ['No BTW required', 'EUR currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP billing', 'Reverse charge support', 'IBAN / SWIFT fields', 'Multi-currency', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorNetherlandsPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Netherlands: Free Factuur Maker for Dutch Freelancers | Invilic"
        description="Free invoice generator (factuur maker) for Dutch businesses and ZZP-ers. Create BTW invoices with 21% VAT or KOR standard invoices in EUR. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-netherlands"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇳🇱 Netherlands
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Netherlands
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free factuur (invoice) generator for Dutch businesses, ZZP-ers, and freelancers. Create BTW invoices with 21% VAT auto-calculated, or standard facturen under the KOR exemption. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-orange-600/20 transition-all duration-200">
              Create BTW Factuur <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create Standard Factuur <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">BTW Factuur vs KOR Invoice in the Netherlands</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-orange-700 dark:text-orange-400 mb-3">Use a BTW Factuur if:</div>
              {[
                'You are BTW-registered with the Belastingdienst',
                'Your client is a BTW-registered business that needs to reclaim VAT',
                'You bill EU business clients (reverse charge / BTW verlegd may apply)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Standard Factuur if:</div>
              {[
                'Your annual Dutch turnover is below EUR 20,000 and you use KOR',
                'You are a ZZP-er or freelancer exempt from BTW registration',
                'Your client does not need a BTW invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Netherlands Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Dutch Factuur for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF factuur for Dutch businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-600/20 transition-all duration-200 text-lg">
            Create Factuur Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
